import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Camera, Car, CheckCircle2 } from 'lucide-react';
import { DAMAGE_KEYS, vehicleDamagesApi } from '../api/vehicleDamages';
import { fmtDate } from '../lib/dates';
import type { DamageVehicle, VehicleDamage } from '../types';
import { Badge, Button, Card, EmptyState, type BadgeTone } from '../components/charte';
import { Sheet } from '../components/shell/Sheet';
import { DamagePhotoStrip } from '../components/vehicle/DamagePhotos';
import { useUiStore } from '../store/ui';

// Véhicules (24/10/2026) : le moniteur signale un dégât repéré sur un véhicule de l'auto-école, consulte
// l'état connu d'un véhicule avant de le prendre et suit ses propres signalements. Le bureau retrouve tout
// dans la fiche du véhicule (« Parc »), avec les leçons faites avec lui autour du constat.

const TONE: Record<VehicleDamage['status'], BadgeTone> = { reported: 'warn', in_repair: 'info', repaired: 'ok', dismissed: 'mute' };
const plate = (v?: { immatriculation: string; brand: string; model: string }) => (v ? `${v.immatriculation} · ${v.brand} ${v.model}` : 'Véhicule');

function DamageCard({ damage, showVehicle }: { damage: VehicleDamage; showVehicle?: boolean }) {
  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          {showVehicle && <p className="text-sm font-semibold text-heading">{plate(damage.vehicle)}</p>}
          <p className="text-[13px] text-secondary">
            {fmtDate(damage.reportedAt)} · {damage.momentLabel} · {damage.areaLabel}
            {!damage.drivable && <span className="font-medium text-danger-dark"> · ne roulait plus</span>}
          </p>
        </div>
        <Badge tone={TONE[damage.status] ?? 'mute'}>{damage.statusLabel}</Badge>
      </div>
      <p className="whitespace-pre-line text-sm text-body">{damage.description}</p>
      <DamagePhotoStrip photos={damage.photos} title={`${damage.areaLabel} · ${fmtDate(damage.reportedAt)}`} max={4} />
    </Card>
  );
}

function VehicleSheet({ vehicle, onClose }: { vehicle: DamageVehicle; onClose: () => void }) {
  const openDamageReport = useUiStore((s) => s.openDamageReport);
  const known = useQuery({ queryKey: DAMAGE_KEYS.known(vehicle.id), queryFn: () => vehicleDamagesApi.known(vehicle.id) });
  return (
    <Sheet
      title={vehicle.immatriculation}
      description={`${vehicle.brand} ${vehicle.model} · dégâts signalés pas encore réparés`}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          icon={Camera}
          className="ml-auto"
          onClick={() => {
            onClose();
            openDamageReport(vehicle.id);
          }}
        >
          Signaler un dégât
        </Button>
      }
    >
      {known.isLoading && <p className="text-sm text-secondary">Chargement…</p>}
      {known.isError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          État du véhicule indisponible.
        </p>
      )}
      {known.data && !known.data.length && (
        <p className="flex items-center gap-2 text-sm text-secondary">
          <CheckCircle2 size={16} className="text-success" aria-hidden /> Aucun dégât signalé en attente de réparation.
        </p>
      )}
      {known.data && known.data.length > 0 && (
        <ul className="flex flex-col gap-2">
          {known.data.map((d) => (
            <li key={d.id}>
              <DamageCard damage={d} />
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}

export default function VehiclesView() {
  const openDamageReport = useUiStore((s) => s.openDamageReport);
  const [shown, setShown] = useState<DamageVehicle | null>(null);
  const vehicles = useQuery({ queryKey: DAMAGE_KEYS.vehicles, queryFn: vehicleDamagesApi.vehicles });
  const mine = useQuery({ queryKey: DAMAGE_KEYS.mine, queryFn: vehicleDamagesApi.mine });
  return (
    <section className="mx-auto grid max-w-3xl gap-5">
      <header className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-title-md text-heading sm:text-title-lg">Véhicules</h1>
          <p className="mt-1 text-sm text-secondary">
            Un défaut repéré en prenant un véhicule ? Prenez-le en photo : l’auto-école suit l’état de chaque véhicule et ses réparations.
          </p>
        </div>
        <Button variant="primary" icon={Camera} onClick={() => openDamageReport()}>
          Signaler un dégât
        </Button>
      </header>

      <div className="grid gap-3">
        <h2 className="text-title-sm text-heading">Véhicules de l’auto-école</h2>
        {vehicles.isLoading && <p className="text-secondary">Chargement…</p>}
        {vehicles.isError && (
          <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
            Impossible de charger les véhicules.
          </p>
        )}
        {vehicles.data && !vehicles.data.vehicles.length && <EmptyState icon={Car} title="Aucun véhicule enregistré par l’auto-école." />}
        {vehicles.data && vehicles.data.vehicles.length > 0 && (
          <ul className="grid gap-2 sm:grid-cols-2">
            {vehicles.data.vehicles.map((v) => (
              <li key={v.id}>
                <Card interactive flush as="div">
                  <button type="button" className="flex w-full items-center gap-3 p-4 text-left" onClick={() => setShown(v)}>
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
                      <Car size={18} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-heading">{v.immatriculation}</span>
                      <span className="block truncate text-[13px] text-secondary">
                        {v.brand} {v.model}
                        {v.id === vehicles.data?.suggestedVehicleId ? ' · votre leçon' : ''}
                      </span>
                    </span>
                    {v.openDamages > 0 ? (
                      <Badge tone="warn">
                        {v.openDamages} dégât{v.openDamages > 1 ? 's' : ''}
                      </Badge>
                    ) : (
                      <Badge tone="ok">RAS</Badge>
                    )}
                  </button>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid gap-3">
        <h2 className="text-title-sm text-heading">Mes signalements</h2>
        {mine.data && !mine.data.length && (
          <EmptyState icon={Camera} title="Aucun signalement envoyé." description="Vos signalements et leur suivi par l’auto-école apparaissent ici." />
        )}
        {mine.data && mine.data.length > 0 && (
          <ul className="grid gap-2">
            {mine.data.map((d) => (
              <li key={d.id}>
                <DamageCard damage={d} showVehicle />
              </li>
            ))}
          </ul>
        )}
      </div>

      {shown && <VehicleSheet vehicle={shown} onClose={() => setShown(null)} />}
    </section>
  );
}
